import React, { useState, useEffect } from 'react';
import { MealItem, mealAPI, userAPI, UserTargets, advisorAPI, AdvisorAnalysis } from '../api';
import { useAuth } from '../contexts/AuthContext';
import Chat from './Chat';
import MealItemEditor from './MealItemEditor';
import DishActionsModal from './DishActionsModal';
import { getCurrentLocalDate, formatTimeLocal } from '../utils/timezone';

const Dashboard: React.FC = () => {
  const [mealItems, setMealItems] = useState<MealItem[]>([]);
  const [selectedDate, setSelectedDate] = useState(getCurrentLocalDate());
  const [isLoading, setIsLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<MealItem | null>(null);
  const [targets, setTargets] = useState<UserTargets>({});
  const [editingTarget, setEditingTarget] = useState<'carbs' | 'protein' | 'fat' | 'calories' | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [analysis, setAnalysis] = useState<AdvisorAnalysis | null>(null);
  const [dishModalOpen, setDishModalOpen] = useState(false);
  const [selectedDishItems, setSelectedDishItems] = useState<MealItem[]>([]);
  const { user, logout } = useAuth();

  const loadMealItems = async (date: string) => {
    setIsLoading(true);
    try {
      const response = await mealAPI.getMealItems(date);
      if (response.success && response.items) {
        setMealItems(response.items);
      }
    } catch (error) {
      console.error('Failed to load meal items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTargets = async () => {
    try {
      const response = await userAPI.getTargets();
      if (response.success) {
        setTargets(response.targets);
      }
    } catch (error) {
      console.error('Failed to load targets:', error);
    }
  };

  const loadAnalysis = async () => {
    try {
      const response = await advisorAPI.getAnalysis();
      if (response.success) {
        setAnalysis(response.analysis);
      }
    } catch (error) {
      console.error('Failed to load analysis:', error);
    }
  };

  useEffect(() => {
    loadMealItems(selectedDate);
    loadTargets();
    loadAnalysis();
  }, [selectedDate]);

  const handleMealItemsUpdated = () => {
    // Refresh meal items when chat actions happen
    loadMealItems(selectedDate);
  };

  const handleMealItemAdded = (newItem: MealItem) => {
    // Refresh the data to get updated dish names and template links
    loadMealItems(selectedDate);
  };

  const handleItemClick = (item: MealItem) => {
    setEditingItem(item);
  };

  const handleItemUpdate = (updatedItem: MealItem) => {
    setMealItems(prev => prev.map(item => 
      item.id === updatedItem.id ? updatedItem : item
    ));
    setEditingItem(null);
  };

  const handleItemDelete = (itemId: number) => {
    setMealItems(prev => prev.filter(item => item.id !== itemId));
    setEditingItem(null);
  };

  const handleEditCancel = () => {
    setEditingItem(null);
  };

  const handleTargetEdit = (macro: 'carbs' | 'protein' | 'fat' | 'calories') => {
    setEditingTarget(macro);
    if (macro === 'calories') {
      // For calories, calculate target from carbs, protein, fat targets
      const calorieTarget = (targets.carbs || 0) * 4 + (targets.protein || 0) * 4 + (targets.fat || 0) * 9;
      setEditValue(calorieTarget.toString());
    } else {
      setEditValue((targets[macro] || 0).toString());
    }
  };

  const handleTargetSave = async () => {
    if (!editingTarget) return;

    const value = parseFloat(editValue);
    if (isNaN(value) || value < 0) {
      return;
    }

    try {
      if (editingTarget === 'calories') {
        // Don't save calories target directly, it's calculated
        setEditingTarget(null);
        setEditValue('');
        return;
      }

      const response = await userAPI.updateTargets({ [editingTarget]: value });
      
      if (response.success) {
        setTargets(prev => ({ ...prev, [editingTarget]: value }));
        setEditingTarget(null);
        setEditValue('');
      }
    } catch (err: any) {
      console.error('Failed to update target:', err);
    }
  };

  const handleTargetCancel = () => {
    setEditingTarget(null);
    setEditValue('');
  };

  const handleDishClick = (dishItems: MealItem[]) => {
    setSelectedDishItems(dishItems);
    setDishModalOpen(true);
  };

  const handleDishRename = async (newName: string) => {
    try {
      // Update the dish template name if it exists
      const dishTemplateId = selectedDishItems[0]?.dish_template_id;
      if (dishTemplateId) {
        await mealAPI.updateDishTemplate(dishTemplateId, { template_name: newName });
      }
      
      // Refresh the items to see changes
      await loadMealItems(selectedDate);
    } catch (error) {
      console.error('Error renaming dish:', error);
    }
  };

  const handleDishDelete = async () => {
    try {
      // Delete all items in the dish
      for (const item of selectedDishItems) {
        if (item.id) {
          await mealAPI.deleteMealItem(item.id);
        }
      }
      
      // Refresh the items
      await loadMealItems(selectedDate);
    } catch (error) {
      console.error('Error deleting dish:', error);
    }
  };

  const calculateTotals = () => {
    return mealItems.reduce(
      (totals, item) => ({
        carbs: totals.carbs + item.macros.carbs,
        protein: totals.protein + item.macros.protein,
        fat: totals.fat + item.macros.fat,
        calories: totals.calories + item.macros.calories,
      }),
      { carbs: 0, protein: 0, fat: 0, calories: 0 }
    );
  };

  // Create a unified timeline of dishes and standalone items
  const createTimelineItems = () => {
    const grouped: { [key: string]: MealItem[] } = {};
    const standalone: MealItem[] = [];
    
    // First, group items by dish session
    mealItems.forEach(item => {
      if (item.dish_session_id) {
        if (!grouped[item.dish_session_id]) {
          grouped[item.dish_session_id] = [];
        }
        grouped[item.dish_session_id].push(item);
      } else {
        standalone.push(item);
      }
    });
    
    // Create timeline items (each item has a timestamp and type)
    const timelineItems: Array<{
      type: 'dish' | 'standalone';
      timestamp: string;
      data: MealItem[] | MealItem;
    }> = [];
    
    // Add grouped dishes (use the earliest timestamp from the group)
    Object.entries(grouped).forEach(([sessionId, items]) => {
      const earliestTimestamp = items.reduce((earliest, item) => 
        item.meal_time < earliest ? item.meal_time : earliest, 
        items[0].meal_time
      );
      
      timelineItems.push({
        type: 'dish',
        timestamp: earliestTimestamp,
        data: items
      });
    });
    
    // Add standalone items
    standalone.forEach(item => {
      timelineItems.push({
        type: 'standalone',
        timestamp: item.meal_time,
        data: item
      });
    });
    
    // Sort by timestamp (most recent first)
    timelineItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    return timelineItems;
  };

  // Infer dish name from grouped items
  const inferDishName = (items: MealItem[]): string => {
    const foodNames = items.map(item => item.food_name.toLowerCase());
    
    // Check if any item explicitly mentions the dish name
    const explicitDishNames = ['sandwich', 'salad', 'smoothie', 'bowl', 'wrap', 'burger', 'pizza', 'pasta', 'soup', 'stew'];
    for (const dishName of explicitDishNames) {
      if (foodNames.some(name => name.includes(dishName))) {
        // Look for more specific dish types
        if (dishName === 'sandwich') {
          // Check for breakfast sandwich indicators
          if (foodNames.some(name => name.includes('egg') || name.includes('sausage') || name.includes('bacon') || name.includes('ham')) &&
              foodNames.some(name => name.includes('muffin') || name.includes('biscuit') || name.includes('english muffin'))) {
            return '🥪 Breakfast Sandwich';
          }
          // Check for other specific sandwich types
          if (foodNames.some(name => name.includes('turkey') || name.includes('ham') || name.includes('roast beef'))) {
            return '🥪 Deli Sandwich';
          }
          if (foodNames.some(name => name.includes('grilled') && name.includes('cheese'))) {
            return '🥪 Grilled Cheese';
          }
          if (foodNames.some(name => name.includes('blt') || (name.includes('bacon') && name.includes('lettuce') && name.includes('tomato')))) {
            return '🥪 BLT';
          }
          return '🥪 Sandwich';
        }
        if (dishName === 'salad') return '🥗 Salad';
        if (dishName === 'smoothie') return '🥤 Smoothie';
        if (dishName === 'bowl') return '🍜 Bowl';
        if (dishName === 'wrap') return '🌯 Wrap';
        if (dishName === 'burger') return '🍔 Burger';
        if (dishName === 'pizza') return '🍕 Pizza';
        if (dishName === 'pasta') return '🍝 Pasta';
        if (dishName === 'soup') return '🍲 Soup';
        if (dishName === 'stew') return '🍲 Stew';
        return `🍽️ ${dishName.charAt(0).toUpperCase() + dishName.slice(1)}`;
      }
    }
    
    // Infer dish types from ingredient combinations
    // Breakfast sandwich detection
    if (foodNames.some(name => name.includes('bread') || name.includes('muffin') || name.includes('biscuit') || name.includes('english muffin')) && 
        foodNames.some(name => name.includes('egg') || name.includes('sausage') || name.includes('bacon') || name.includes('ham')) &&
        foodNames.some(name => name.includes('cheese'))) {
      return '🥪 Breakfast Sandwich';
    }
    
    // Regular sandwich detection
    if (foodNames.some(name => name.includes('bread') || name.includes('roll') || name.includes('bun')) && 
        foodNames.some(name => name.includes('meat') || name.includes('turkey') || name.includes('ham') || name.includes('chicken') || name.includes('cheese'))) {
      return '🥪 Sandwich';
    }
    
    // Salad detection
    if (foodNames.some(name => name.includes('salad')) || 
        (foodNames.some(name => name.includes('lettuce') || name.includes('greens') || name.includes('spinach')) && 
         items.length >= 3)) {
      if (foodNames.some(name => name.includes('chicken'))) {
        return '🥗 Chicken Salad';
      }
      if (foodNames.some(name => name.includes('caesar'))) {
        return '🥗 Caesar Salad';
      }
      return '🥗 Salad';
    }
    
    // Smoothie detection
    if (foodNames.some(name => name.includes('smoothie')) || 
        (foodNames.some(name => name.includes('protein') || name.includes('whey')) && 
         foodNames.some(name => name.includes('fruit') || name.includes('berry') || name.includes('banana')))) {
      return '🥤 Smoothie';
    }
    
    // Bowl detection
    if (foodNames.some(name => name.includes('bowl')) || 
        (foodNames.some(name => name.includes('rice') || name.includes('quinoa') || name.includes('grain')) && 
         items.length >= 3)) {
      return '🍜 Bowl';
    }
    
    // Generic fallback for multi-ingredient items
    if (items.length >= 3) {
      return '🍽️ Dish';
    }
    
    return '🍽️ Dish';
  };

  // Calculate totals for a group of items
  const calculateGroupTotals = (items: MealItem[]) => {
    return items.reduce(
      (totals, item) => ({
        carbs: totals.carbs + item.macros.carbs,
        protein: totals.protein + item.macros.protein,
        fat: totals.fat + item.macros.fat,
        calories: totals.calories + item.macros.calories,
      }),
      { carbs: 0, protein: 0, fat: 0, calories: 0 }
    );
  };

  const getCalorieTarget = () => {
    return (targets.carbs || 0) * 4 + (targets.protein || 0) * 4 + (targets.fat || 0) * 9;
  };

  const getProgressColor = (current: number, target?: number) => {
    if (!target) return 'bg-gray-500';
    const percentage = (current / target) * 100;
    if (percentage >= 90 && percentage <= 110) return 'bg-green-500';
    if (percentage >= 80 && percentage <= 120) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getProgressPercentage = (current: number, target?: number) => {
    if (!target) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getProgressBorderColor = (current: number, target?: number) => {
    if (!target) return 'border-gray-200/50';
    const percentage = (current / target) * 100;
    if (percentage >= 90 && percentage <= 110) return 'border-green-300/60';
    if (percentage >= 80 && percentage <= 120) return 'border-yellow-300/60';
    return 'border-red-300/60';
  };

  const renderMacroCard = (
    macro: 'carbs' | 'protein' | 'fat' | 'calories',
    label: string,
    value: number,
    target: number | undefined,
    baseColorClass: string,
    unit: string = 'g'
  ) => {
    const hasTarget = target !== undefined && target > 0;
    const progressPercentage = getProgressPercentage(value, target);
    const progressColor = getProgressColor(value, target);
    const borderColor = getProgressBorderColor(value, target);
    
    return (
      <div className={`bg-gradient-to-br ${baseColorClass} p-4 rounded-2xl border ${borderColor} transition-all duration-300`}>
        {/* Content */}
        <div>
          <div className="text-xs sm:text-sm font-medium mb-1 opacity-80">{label}</div>
          <div className="text-xl sm:text-2xl font-bold mb-1">
            {editingTarget === macro ? (
              <div className="flex items-center space-x-1">
                <input
                  type="number"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-16 px-1 py-0.5 text-sm border border-gray-300 rounded bg-white text-gray-900"
                  placeholder="0"
                  min="0"
                  step="0.1"
                  disabled={macro === 'calories'}
                />
                <button
                  onClick={handleTargetSave}
                  className="text-green-600 hover:text-green-800 text-sm"
                >
                  ✓
                </button>
                <button
                  onClick={handleTargetCancel}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  ✕
                </button>
              </div>
            ) : hasTarget ? (
              <span 
                className="cursor-pointer hover:opacity-80"
                onClick={() => handleTargetEdit(macro)}
              >
                {value.toFixed(1)}/{target}
                <span className="text-sm sm:text-base font-normal">{unit}</span>
              </span>
            ) : (
              <div className="flex items-center space-x-2">
                <span>
                  {value.toFixed(1)}
                  <span className="text-sm sm:text-base font-normal">{unit}</span>
                </span>
                <button
                  onClick={() => handleTargetEdit(macro)}
                  className="px-2 py-1 bg-white bg-opacity-50 text-xs rounded-lg hover:bg-opacity-70 transition-colors"
                >
                  Set Target
                </button>
              </div>
            )}
          </div>
          {hasTarget && (
            <div className="w-full bg-white bg-opacity-40 rounded-full h-2 mt-2">
              <div 
                className={`h-2 ${progressColor} rounded-full transition-all duration-500 ease-out`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          )}
          {hasTarget && (
            <div className="text-xs mt-1 opacity-70">
              {progressPercentage.toFixed(0)}% of target
            </div>
          )}
        </div>
      </div>
    );
  };

  const totals = calculateTotals();
  const calorieTarget = getCalorieTarget();
  const hasTargets = !!(targets.carbs && targets.protein && targets.fat);
  const timelineItems = createTimelineItems();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              MacroBot
            </h1>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                Welcome, {user?.email}
              </span>
              <button
                onClick={logout}
                className="text-xs sm:text-sm px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-3 sm:py-6 px-3 sm:px-6 lg:px-8">
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Chat Section */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></span>
                  Nutrition Assistant
                </h2>
              </div>
              <Chat
                onMealItemsUpdated={handleMealItemsUpdated}
                onMealItemAdded={handleMealItemAdded}
                selectedDate={selectedDate}
              />
            </div>

            {/* Daily Summary */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  Daily Summary
                </h2>
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {selectedDate !== getCurrentLocalDate() && (
                    <button
                      onClick={() => setSelectedDate(getCurrentLocalDate())}
                      className="px-3 py-2 bg-blue-500 text-white rounded-xl text-sm hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                      go to Today
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
                {renderMacroCard(
                  'carbs',
                  'Carbs',
                  totals.carbs,
                  targets.carbs,
                  'from-blue-50 to-blue-100/50 border-blue-200/50 text-blue-900'
                )}
                {renderMacroCard(
                  'protein',
                  'Protein',
                  totals.protein,
                  targets.protein,
                  'from-green-50 to-green-100/50 border-green-200/50 text-green-900'
                )}
                {renderMacroCard(
                  'fat',
                  'Fat',
                  totals.fat,
                  targets.fat,
                  'from-amber-50 to-amber-100/50 border-amber-200/50 text-amber-900'
                )}
                {renderMacroCard(
                  'calories',
                  'Calories',
                  totals.calories,
                  calorieTarget > 0 ? calorieTarget : undefined,
                  'from-purple-50 to-purple-100/50 border-purple-200/50 text-purple-900',
                  ' cal'
                )}
              </div>

              {/* Meals List */}
              <div className="space-y-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Today's Food Items</h3>
                {isLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    Loading meal items...
                  </div>
                ) : mealItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-2xl">
                    <div className="text-4xl mb-2">🍽️</div>
                    <p>No food items logged for this date</p>
                    <p className="text-xs mt-1">Start by describing your food above!</p>
                  </div>
                ) : (
                  <>
                    {/* Timeline of Dishes and Standalone Items */}
                    {timelineItems.map((timelineItem, index) => {
                      if (timelineItem.type === 'dish') {
                        const items = timelineItem.data as MealItem[];
                        const dishName = items[0].dish_name || 'Mixed Items';
                        const dishTotals = calculateGroupTotals(items);
                        const dishTime = formatTimeLocal(items[0].meal_time);
                        
                        return (
                          <div
                            key={`dish-${items[0].dish_session_id}-${index}`}
                            className="bg-gradient-to-r from-indigo-50 to-purple-50/50 border border-indigo-200/50 rounded-2xl p-4 transition-all duration-200"
                          >
                            {/* Dish Header */}
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1 pr-3">
                                <div 
                                  className="text-sm sm:text-base font-semibold text-indigo-900 leading-tight cursor-pointer hover:text-indigo-700 transition-colors"
                                  onClick={() => handleDishClick(items)}
                                  title="Click to rename or delete dish"
                                >
                                  {dishName}
                                  {items.some(item => item.is_variation) && (
                                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                      Variation
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-indigo-600 mt-1 flex items-center">
                                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full mr-2"></span>
                                  {dishTime} • {items.length} ingredients
                                </div>
                              </div>
                              <div className="text-xs sm:text-sm text-indigo-700 text-right bg-indigo-100/60 rounded-xl p-2 min-w-[80px]">
                                <div className="font-medium text-blue-700">C: {dishTotals.carbs.toFixed(1)}g</div>
                                <div className="font-medium text-green-700">P: {dishTotals.protein.toFixed(1)}g</div>
                                <div className="font-medium text-amber-700">F: {dishTotals.fat.toFixed(1)}g</div>
                                <div className="font-bold text-purple-700 mt-1 text-sm">{Math.round(dishTotals.calories)} cal</div>
                              </div>
                            </div>
                            
                            {/* Ingredients */}
                            <div className="space-y-2 ml-4">
                              {items.map((item) => (
                                <div
                                  key={item.id}
                                  onClick={() => handleItemClick(item)}
                                  className="bg-white/70 border border-indigo-200/30 rounded-xl p-3 cursor-pointer hover:shadow-sm hover:bg-white/90 transition-all duration-200"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1 pr-3">
                                      <div className="text-sm font-medium text-gray-900 leading-tight">
                                        {item.food_name}
                                        {item.qty && item.unit && (
                                          <span className="text-gray-500 ml-2">({item.qty} {item.unit})</span>
                                        )}
                                      </div>
                                      {item.notes && (
                                        <div className="text-xs text-gray-600 mt-1">{item.notes}</div>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-600 text-right bg-gray-50 rounded-lg p-1.5 min-w-[70px]">
                                      <div className="font-medium text-blue-700">C: {item.macros.carbs.toFixed(1)}g</div>
                                      <div className="font-medium text-green-700">P: {item.macros.protein.toFixed(1)}g</div>
                                      <div className="font-medium text-amber-700">F: {item.macros.fat.toFixed(1)}g</div>
                                      <div className="font-bold text-purple-700 mt-1">{Math.round(item.macros.calories)} cal</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      } else {
                        // Standalone item
                        const item = timelineItem.data as MealItem;
                        return (
                          <div
                            key={`standalone-${item.id}-${index}`}
                            onClick={() => handleItemClick(item)}
                            className="bg-gradient-to-r from-white to-gray-50/50 border border-gray-200/50 rounded-2xl p-4 cursor-pointer hover:shadow-md hover:from-blue-50/50 hover:to-blue-100/25 transition-all duration-200"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 pr-3">
                                <div className="text-sm sm:text-base font-medium text-gray-900 leading-tight">
                                  {item.food_name}
                                  {item.qty && item.unit && (
                                    <span className="text-gray-500 ml-2">({item.qty} {item.unit})</span>
                                  )}
                                </div>
                                {item.notes && (
                                  <div className="text-xs text-gray-600 mt-1">{item.notes}</div>
                                )}
                                <div className="text-xs text-gray-500 mt-2 flex items-center">
                                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></span>
                                  {formatTimeLocal(item.meal_time)}
                                </div>
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600 text-right bg-gray-50 rounded-xl p-2 min-w-[80px]">
                                <div className="font-medium text-blue-700">C: {item.macros.carbs.toFixed(1)}g</div>
                                <div className="font-medium text-green-700">P: {item.macros.protein.toFixed(1)}g</div>
                                <div className="font-medium text-amber-700">F: {item.macros.fat.toFixed(1)}g</div>
                                <div className="font-bold text-purple-700 mt-1 text-sm">{Math.round(item.macros.calories)} cal</div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {editingItem && (
        <MealItemEditor
          item={editingItem}
          onUpdate={handleItemUpdate}
          onDelete={handleItemDelete}
          onCancel={handleEditCancel}
        />
      )}

      {/* Dish Actions Modal */}
      <DishActionsModal
        isOpen={dishModalOpen}
        onClose={() => setDishModalOpen(false)}
        dishItems={selectedDishItems}
        onRename={handleDishRename}
        onDelete={handleDishDelete}
      />
    </div>
  );
};

export default Dashboard;